import { useEffect, useState, useSyncExternalStore } from 'react';
import { APPEARANCE_PREVIEW_QUERY } from '@/theme/tokens';

let previewSession = false;

/** Appearance iframe, or a public page after the server marked an admin session. */
export function inlineEditingEnabled() {
    if (typeof window === 'undefined') return false;
    if (window.__ADMIN__) return true;
    if (previewSession) return true;
    if (new URLSearchParams(window.location.search).get(APPEARANCE_PREVIEW_QUERY) === '1') {
        previewSession = true;
        return true;
    }
    return false;
}

/**
 * Whether the editor around this frame offers tools that change a file
 * everywhere it is used (rebuilding an image's sizes). The editor turns
 * them off for content editors.
 */
export function frameFileTools() {
    if (typeof window === 'undefined') return false;
    try {
        return window.parent?.__EDITOR_FILE_TOOLS__ !== false;
    } catch {
        return true;
    }
}

// A page that can't be changed here (a content editor's page that comes from
// the live site) shows as visitors see it. The editor posts each change
// (PAGE_READ_ONLY_MESSAGE); a fresh frame reads the editor's flag.
let frameReadOnly = null;
const readOnlyListeners = new Set();

function readOnlyNow() {
    if (frameReadOnly === null) {
        try {
            frameReadOnly = window.parent?.__PAGE_READ_ONLY__ === true;
        } catch {
            frameReadOnly = false;
        }
    }
    return frameReadOnly;
}

export function setFrameReadOnly(next) {
    const value = next === true;
    if (value === readOnlyNow()) return;
    frameReadOnly = value;
    readOnlyListeners.forEach((listener) => listener());
    // Page text listens for this to turn its editing on or off.
    document.dispatchEvent(new Event('portfolio:edit-group'));
}

const subscribeReadOnly = (listener) => {
    readOnlyListeners.add(listener);
    return () => readOnlyListeners.delete(listener);
};

/** Inline editing is on and the open page may be changed. */
export function frameEditable() {
    return inlineEditingEnabled() && !readOnlyNow();
}

/**
 * True once mounted inside the Appearance preview frame, while the open page
 * may be changed. Starts false so the server render and hydration match, and
 * is never true on the live site.
 */
export function useEditorFrame() {
    const [inFrame, setInFrame] = useState(false);
    const readOnly = useSyncExternalStore(subscribeReadOnly, readOnlyNow, () => false);
    useEffect(() => {
        setInFrame(window.parent !== window && inlineEditingEnabled());
    }, []);
    return inFrame && !readOnly;
}

// Editors that hold back changes (a debounce) register here so Save can ask
// for them before it snapshots the draft.
const pendingFlushers = new Set();

/** @returns {() => void} unregister */
export function registerEditFlush(flush) {
    pendingFlushers.add(flush);
    return () => pendingFlushers.delete(flush);
}

/** Emit every held-back edit now. */
export function flushPendingEdits() {
    pendingFlushers.forEach((flush) => {
        try { flush(); } catch (error) { console.warn('[appearance-preview] flush failed', error); }
    });
}

// The block the editor has opened for editing. Blocks render as visitors see
// them until the user explicitly enters one; the preview bridge sets this.
let editingBlockId = '';
const editingListeners = new Set();

export function setEditingBlock(id) {
    const next = id || '';
    if (next === editingBlockId) return;
    editingBlockId = next;
    editingListeners.forEach((listener) => listener());
}

/** The block open for editing in the preview now, or ''. */
export const editingBlock = () => editingBlockId;

const subscribeEditing = (listener) => {
    editingListeners.add(listener);
    return () => editingListeners.delete(listener);
};

/** True while this block is open for editing in the Appearance preview. */
export function useBlockEditing(blockId) {
    return useSyncExternalStore(
        subscribeEditing,
        () => Boolean(blockId) && editingBlockId === blockId,
        () => false,
    );
}

/** True while any block is open for editing in the Appearance preview. */
export function useAnyBlockEditing() {
    return useSyncExternalStore(
        subscribeEditing,
        () => Boolean(editingBlockId),
        () => false,
    );
}
